import type { FlowSubscription } from '@adv-frontend/modules-flow';

/**
 * Создает коллекцию слушателей с управлением жизненным циклом.
 *
 * @param options - Опции коллекции
 * @returns Объект для управления коллекцией слушателей
 */
export function createListenersCollection({ onAdd, onActivate }: ListenersCollectionOptions) {
    let listenerCount = 0;
    let deactivate: (() => void) | null = null;
    // Подписки, добавленные за время выполнения onActivate(). Не null только пока идет активация.
    let activationSubscriptions: FlowSubscription[] | null = null;
    // Номер поколения, растет на каждом reset(). Подписка помнит свое: выданную до сброса
    // счетчик уже не учитывает, и вычитать ее повторно нельзя.
    let generation = 0;

    return {
        add(listener: () => void): FlowSubscription {
            // Слушателя регистрируем до активации: onActivate() вправе позвать слушателей прямо
            // здесь, и добавляемый должен получить этот вызов.
            const listenerSubscription = onAdd(listener);

            listenerCount++;

            const subscriptionGeneration = generation;
            let active = true;
            const subscription: FlowSubscription = {
                unsubscribe() {
                    if (!active) {
                        return;
                    }

                    active = false;

                    // Вызывающий просил отписку от источника, ее ошибку он и получит. Счетчик
                    // слушателей и деактивацию обновляем в любом случае, а их ошибки этой уступают
                    let sourceError: { err: unknown } | null = null;

                    try {
                        listenerSubscription.unsubscribe();
                    } catch (err) {
                        sourceError = { err };
                    }

                    try {
                        // Счетчик и деактивацию обновляем и после падения отписки: повторить это
                        // уже нельзя — коллекция навсегда осталась бы со слушателями по счетчику,
                        // но без активации.
                        //
                        // Подписку, пережившую reset(), счетчик не учитывает: он обнулен вместе с ней.
                        if (subscriptionGeneration === generation) {
                            listenerCount--;

                            if (listenerCount === 0) {
                                // Cleanup отцепляем до вызова: упади он, коллекция все равно
                                // останется деактивированной, а не с чужим deactivate наготове
                                const cleanup = deactivate;
                                deactivate = null;
                                cleanup?.();
                            }
                        }
                    } catch (err) {
                        if (!sourceError) {
                            throw err;
                        }

                        // Источник упал первым, его ошибку и пробрасываем вызывающему ниже.
                        // Ошибку cleanup только логируем, чтобы она не пропала совсем
                        console.error(err);
                    }

                    if (sourceError) {
                        throw sourceError.err;
                    }
                },
            };

            if (activationSubscriptions) {
                // Добавление изнутри onActivate(): активация уже идет, повторять ее не нужно.
                // Но упади она — снять надо и эту подписку тоже.
                activationSubscriptions.push(subscription);
            } else if (listenerCount === 1) {
                const addedDuringActivation = [subscription];
                const activationGeneration = generation;
                activationSubscriptions = addedDuringActivation;
                // Cleanup отмененной активации: заполняется, только если ее отменил reset()
                let cancelledCleanup: (() => void) | null = null;

                try {
                    const cleanup = onActivate();

                    if (generation === activationGeneration) {
                        deactivate = cleanup;
                    } else {
                        // Активацию отменил reset() изнутри onActivate(): слушателя, ради которого
                        // активировались, счетчик уже не учитывает. Сохрани мы cleanup — звать его
                        // стало бы некому, поэтому снимаем побочные эффекты активации сразу.
                        cancelledCleanup = cleanup;
                    }
                } catch (err) {
                    // Откатываем все подписки, появившиеся за время неудачной активации: и ту, ради
                    // которой активировались (объект подписки вызывающему не возвращается, снять ее
                    // было бы некому), и добавленные изнутри onActivate() — он вправе синхронно
                    // позвать уже зарегистрированного слушателя, а тот подписаться еще раз.
                    // Без этого счетчик остался бы ненулевым без активации, и следующий add()
                    // ее уже никогда бы не запустил.
                    //
                    // Альтернатива — оставить подписки и повторить активацию — не годится:
                    // onActivate() успел выполниться частично, а вернуть свои побочные эффекты
                    // он умеет только через deactivate, которого при исключении нет.
                    for (const addedSubscription of addedDuringActivation) {
                        try {
                            addedSubscription.unsubscribe();
                        } catch (rollbackError) {
                            // Откат доводим до конца: упади он на одной подписке, остальные
                            // остались бы в счетчике без активации за ними — ровно та блокировка,
                            // от которой откат и защищает. Вызывающему пробрасываем ошибку
                            // активации, а ошибку отката только логируем
                            console.error(rollbackError);
                        }
                    }

                    throw err;
                } finally {
                    activationSubscriptions = null;
                }

                if (cancelledCleanup) {
                    // Зовем уже за пределами блока активации: список подписок для отката сброшен,
                    // и падение cleanup не должно откатывать подписки, добавленные после reset() —
                    // они живут в новом поколении и к отмененной активации отношения не имеют.
                    // Снимаем только текущую: ее объект вызывающему уже не вернется.
                    try {
                        cancelledCleanup();
                    } catch (err) {
                        try {
                            subscription.unsubscribe();
                        } catch (rollbackError) {
                            // Вызывающему пробрасываем ошибку cleanup, а ошибку отката
                            // только логируем
                            console.error(rollbackError);
                        }

                        throw err;
                    }
                }
            }

            return subscription;
        },
        hasListeners() {
            return listenerCount > 0;
        },
        reset() {
            const cleanup = deactivate;

            // Свое состояние приводим в порядок до вызова cleanup: упади он между сменой поколения
            // и обнулением, счетчик так и остался бы ненулевым, а вычесть из него выданные
            // подписки стало бы нечем — их поколение больше не совпадает. Коллекция навсегда
            // осталась бы со слушателями по счетчику, но без активации.
            //
            // Смена поколения: все выданные до сих пор подписки перестают попадать в счетчик,
            // иначе их отписка увела бы его в минус и коллекция больше никогда не активировалась
            // бы. Если reset() пришел изнутри onActivate(), это заодно помечает активацию
            // отмененной.
            deactivate = null;
            listenerCount = 0;
            generation++;

            cleanup?.();
        },
    };
}

/**
 * Опции для создания коллекции слушателей.
 */
export interface ListenersCollectionOptions {
    /**
     * Callback при добавлении слушателя
     * @param listener - Callback слушателя
     * @returns Объект для управления подпиской
     */
    onAdd(listener: () => void): FlowSubscription;
    /**
     * Callback при добавлении первого слушателя. Вызывается после его регистрации, поэтому
     * смена значения потока внутри onActivate дойдет до этого слушателя. Если он бросит
     * исключение, все подписки, появившиеся за время активации, будут сняты.
     * @returns Callback при удалении последнего слушателя. Если за время активации коллекцию
     * сбросят через reset(), он будет вызван сразу же по возврату.
     */
    onActivate(): () => void;
}
